/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    getCollaterals,
    postApplicationCollateral,
    postApplicationCollateralsAssets,
} from 'api/application'
import createPostCollateralDTO, {
    PostCollateralDTO,
} from 'api/application/createPostCollateralDTO'
import deleteApplicationCollateral from 'api/application/deleteApplicationCollateral'
import { createPostOpeCollateralsAssetsDTO } from 'api/application/postOpeApplicationCollateralsAssets'
import { getVehicleDetails } from 'api/vehicles/getVehicles'

const deleteApplicationCollaterals = async (applicationUuid: string) => {
    const collaterals = await getCollaterals(applicationUuid)

    const promises = collaterals.map((collateral) =>
        deleteApplicationCollateral(applicationUuid, collateral.id.toString()),
    )

    return Promise.all(promises)
}

const makeTitleInformation = (vehicleData: any) => {
    return {
        has_title: vehicleData.hasTitle === true,
        no_title_reason:
            vehicleData.noTitleReason !== ''
                ? vehicleData.noTitleReason
                : undefined,
        no_title_other_reason:
            vehicleData.noTitleOtherReason !== ''
                ? vehicleData.noTitleOtherReason
                : undefined,
    }
}

const makeTrailerCollateral = (vehicleData: any) => {
    const mileage = vehicleData.mileage
        ? parseInt(vehicleData.mileage, 10)
        : undefined

    return {
        asset_type: 'powersports_vehicle',
        asset_value: vehicleData.trailerValue,
        // Data source is set as 'los_asset' instead of 'write_in' to be
        // compatible with legacy trailer implementation.
        data_source: 'los_asset',
        asset: {
            vehicle_type: 'trailer',
            condition: vehicleData.condition === 'new' ? 'N' : 'U',
            make: vehicleData.make,
            year: vehicleData.year,
            model: vehicleData.trailerModel,
            weight: vehicleData.trailerWeight,
            title_information: makeTitleInformation(vehicleData),
            mileage,
        },
    }
}

const makeVehicleCollateral = async (vehicleData: any) => {
    const vehicleId = vehicleData.selectedVehicleId.split(':')[2]
    const vehicle = await getVehicleDetails(vehicleId)

    const mileage = vehicleData.mileage
        ? parseInt(vehicleData.mileage, 10)
        : undefined

    const collateral = createPostCollateralDTO(
        vehicle,
        null,
    ) as PostCollateralDTO

    collateral.asset.mileage = mileage
    collateral.asset.title_information = makeTitleInformation(vehicleData)

    return Promise.resolve(collateral)
}

// `createPostOpeCollateralsAssetsDTO` only ever reads `vehicle.opeUnits`.
// When the opeUnits-based equipment selection UI isn't active (e.g.
// `enableOpeSearchAsYouType` off), the legacy single-field UI only writes to
// `vehicle.primary`, so `opeUnits` must be built here before calling the DTO
// builder. OPE multi-collateral support is exclusively provided by the
// opeUnits-based UI (gated behind `enableOpeMultiCollateralUi`), so there is
// no `vehicle.secondary`/`vehicle.multiUnit` case to handle here.
function buildOpeUnits(vehicleValues: any) {
    return vehicleValues.opeUnits ?? [vehicleValues.primary]
}

export default async function setApplicationCollaterals(
    applicationUuid: string,
    vehicleValues: any,
    deleteExisting = false,
    useCollateralsAssetsEndpoint = false,
) {
    const {
        condition,
        makeId,
        isTrailer,
        multiUnit,
        primary: primaryVehicle,
        secondary: secondaryVehicle,
    } = vehicleValues

    if (useCollateralsAssetsEndpoint) {
        // This endpoint replaces the application's collateral set atomically,
        // including when deleteExisting is true; legacy deletion must not run.
        await postApplicationCollateralsAssets(
            applicationUuid,
            createPostOpeCollateralsAssetsDTO({
                ...vehicleValues,
                opeUnits: buildOpeUnits(vehicleValues),
            }),
        )

        return
    }

    const collaterals: PostCollateralDTO[] = []

    if (isTrailer) {
        collaterals.push(
            makeTrailerCollateral({
                ...primaryVehicle,
                condition,
                makeId,
            }),
        )
    } else {
        collaterals.push(await makeVehicleCollateral(primaryVehicle))

        if (multiUnit && secondaryVehicle?.selectedVehicleId) {
            collaterals.push(await makeVehicleCollateral(secondaryVehicle))
        }
    }

    if (deleteExisting) {
        await deleteApplicationCollaterals(applicationUuid)
    }

    /**
     * The collaterals must be saved in serial to prevent unexpected
     * behaviors such as multiple UCC Title Decisions being created
     * for used multi-unit applications.
     *
     * See https://octane.atlassian.net/browse/FIKA-2899
     *
     * Accomplish this by using reduce instead of a for...of as it's
     * not recommended by eslint due to it's large polyfill size.
     */
    await collaterals.reduce(async (a, collateral) => {
        await a
        await postApplicationCollateral(applicationUuid, collateral)
    }, Promise.resolve())
}
